/* eslint-disable react-refresh/only-export-components */
import { type AuthenticationResult, EventType } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { WebAuth } from 'auth0-js';
import React, { createContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  authenticate,
  logout as logoutAction,
  setUserInformation,
} from '../redux/auth/auth-slice';
import { isAdb2cEnabled } from '../utils/feature-flags';

type AuthContextType = {
  logout: () => void;
};

export const defaultAuthContext: AuthContextType = {
  logout: () => {},
};

export const AuthContext = createContext<AuthContextType>(defaultAuthContext);

const AuthService: React.FC<{ children: React.ReactNode }> = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useDispatch();
  const { instance } = useMsal();

  const auth0 = useMemo(() => {
    return new WebAuth({
      domain: import.meta.env.VITE_AUTH0_DOMAIN,
      clientID: import.meta.env.VITE_AUTH0_CLIENT_ID,
      redirectUri: `${window.location.origin}/callback`,
      audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      responseType: 'id_token token',
    });
  }, []);

  useEffect(() => {
    if (isAdb2cEnabled()) {
      // Bridge MSAL auth events to Redux
      const callbackId = instance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const result = event.payload as AuthenticationResult;
          const claims = result.idTokenClaims as Record<string, unknown>;

          dispatch(
            authenticate({
              accessToken: result.accessToken,
              expiresAt: result.expiresOn ? result.expiresOn.getTime() : 0,
              authId: (claims.sub as string) || (claims.oid as string) || '',
            }),
          );

          dispatch(
            setUserInformation({
              firstName: (claims.given_name as string) || '',
              lastName: (claims.family_name as string) || '',
              emailVerified: true,
              email: (claims.username as string) || '',
            }),
          );
        }
      });

      return () => {
        if (callbackId) {
          instance.removeEventCallback(callbackId);
        }
      };
    } else {
      // Auth0: parse hash from redirect (implicit / hybrid flow)
      auth0.parseHash({ hash: window.location.hash }, (err, authResult) => {
        if (!err && authResult && authResult.expiresIn) {
          dispatch(
            authenticate({
              expiresAt: authResult.expiresIn * 1000 + Date.now(),
              accessToken: authResult.accessToken as string,
              authId: authResult.idTokenPayload.sub,
            }),
          );
          dispatch(
            setUserInformation({
              firstName: authResult.idTokenPayload.given_name,
              lastName: authResult.idTokenPayload.family_name,
              emailVerified: authResult.idTokenPayload.email_verified || false,
              email: authResult.idTokenPayload.email,
            }),
          );
        }
      });
    }
  }, [instance, auth0, dispatch]);

  const logout = () => {
    dispatch(logoutAction());
    if (isAdb2cEnabled()) {
      const activeAccount = instance.getActiveAccount();
      if (activeAccount) {
        instance.logoutRedirect({ account: activeAccount });
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        logout,
      }}
    >
      {[children]}
    </AuthContext.Provider>
  );
};

export default AuthService;
